import { Injectable, NgZone } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpInterceptor,
  HttpEvent,
  HttpResponse,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { AuthService } from '../modules/auth';
import { SsoService } from '../modules/auth/services/sso.service';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { EmbedSessionState } from '../shared/embed-session.state';

@Injectable()
export class CommonInterceptorInterceptor implements HttpInterceptor {

  constructor(
    private authService: AuthService,
    private sso: SsoService,
    private toastr: ToastrService,
    private router: Router,
    private zone: NgZone,
    private embed: EmbedSessionState,
  ) { }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<any> {
    // The embedded editor: its session token instead of a bearer, the gateway envelope unwrapped,
    // and nothing else. Errors are the page's business: no panel toasts, and above all no
    // logout, which would send someone without a Templatify user to the Keycloak login.
    if (this.embed.active) {
      const embedded = this.embed.token ? request.clone({ setHeaders: { 'X-Editor-Session': this.embed.token } }) : request;
      return next.handle(embedded).pipe(
        map((event: HttpEvent<any>) => {
          const isBinary = typeof Blob !== 'undefined' && event instanceof HttpResponse && event.body instanceof Blob;
          const wrapped = event instanceof HttpResponse && event.body && !isBinary && typeof event.body === 'object' && 'Response' in event.body;
          return wrapped ? (event as HttpResponse<any>).clone({ body: (event as HttpResponse<any>).body.Response }) : event;
        }),
        this.enterZone(),
      );
    }

    if (!this.authService.isInterceptorEnabled()) {
      return this.handlePipe(next.handle(request));
    }

    return from(this.sso.updateToken(30)).pipe(
      switchMap(() => {
        const token = this.sso.token ?? localStorage.getItem('authToken')?.replace(/"/g, '');
        const authedRequest = token
          ? request.clone({
              setHeaders: {
                accept: '*/*',
                Authorization: `Bearer ${token}`,
                'Access-Control-Allow-Headers': 'Origin, X-Requested-With, Content-Type, Accept',
              },
            })
          : request;

        return this.handlePipe(next.handle(authedRequest));
      }),
    );
  }

  private handlePipe(stream: Observable<HttpEvent<any>>): Observable<any> {
    return stream.pipe(
      map((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          this.handleSuccess(event);
          // The gateway wraps JSON in { Response, Error }; a Blob (PDF/HTML requested with
          // responseType 'blob') is not wrapped and has no `Response`, so unwrapping it
          // would replace the document with `undefined`.
          const isBinary = typeof Blob !== 'undefined' && event.body instanceof Blob;
          if (event.body && !isBinary && (event.body.Response || event.status === 200)) {
            return event.clone({ body: event.body.Response });
          }
        }
        return event;
      }),
      catchError((error: HttpErrorResponse) => {
        this.handleError(error);
        return throwError(error);
      }),
      this.enterZone(),
    );
  }

  /**
   * The token refresh (`sso.updateToken`) resolves outside Angular's zone, so everything
   * chained after it — the response included — reaches the pages outside the zone and a
   * plain `subscribe(res => this.x = res)` does not repaint the view (the `async` pipe and
   * DataTables callbacks did, which hid the problem). Re-enter the zone once, here, for
   * every response and error.
   */
  private enterZone<T>() {
    return (source: Observable<T>) =>
      new Observable<T>((subscriber) =>
        source.subscribe({
          next: (value) => this.zone.run(() => subscriber.next(value)),
          error: (err) => this.zone.run(() => subscriber.error(err)),
          complete: () => this.zone.run(() => subscriber.complete()),
        }),
      );
  }

  private handleSuccess(response: HttpResponse<any>) {
    switch (response.status) {
      case 200:
        break;
      case 201:
        this.toastr.success('Record created successfully');
        break;
      case 204:
        this.toastr.info('Record updated successfully');
        break;
      default:
        this.toastr.info(`Response received with status: ${response.status}`);
        break;
    }
  }

  private handleError(error: HttpErrorResponse) {
    switch (error.status) {
      case 400:
        break;
      case 401:
        this.handleUnauthorizedError();
        break;
      case 403:
        this.toastr.error('Forbidden');
        break;
      case 404:
        this.toastr.error('Not found');
        break;
      case 500:
        this.toastr.error('Internal server error');
        break;
      default:
        this.toastr.error(`Error occurred with status: ${error.status}`);
        break;
    }
  }

  private isLogoutTriggered = false;
  private handleUnauthorizedError() {
    if (this.isLogoutTriggered) return;

    this.isLogoutTriggered = true;
    this.toastr.error('Unauthorized');
    this.authService.logout();
  }
}
